// Pure helpers for staff notes (spec 059 D8–D20): the note types, the contact chips, the
// Contact report's filters, date range and search mode, and the Students list's Contact cell.
// No React and no Firestore:
// Jest loads this file.
import { dateTimeLines } from "./kitPresentation";
import { toDate } from "./ticketPresentation";

// A note written before D8 has no type: it is a general note.
export const noteType = (note) => (note?.type === "contact" ? "contact" : "general");

export const NOTE_TYPES = [
  { key: "general", label: "General note" },
  { key: "contact", label: "Contact" },
];

export const REPLIED_OPTIONS = [
  { key: "yes", label: "Yes" },
  { key: "no", label: "No" },
];

export const INTEREST_OPTIONS = [
  { key: "yes", label: "Yes" },
  { key: "no", label: "No" },
  { key: "unsure", label: "Not sure" },
];

const INTEREST_CHIPS = {
  yes: { label: "Interested", color: "success" },
  no: { label: "Not interested", color: "error" },
  unsure: { label: "Not sure", color: "warning" },
};

// The chip for an `interested` value, or null for a value the rules do not accept.
export const interestChip = (interested) => INTEREST_CHIPS[interested] || null;

// A contact note's chips, in StatusChip's tones: No reply; or Replied and the interest.
export const contactChips = (note) => {
  if (noteType(note) !== "contact") return [];
  if (!note.replied) return [{ label: "No reply", color: "default" }];
  return [{ label: "Replied", color: "info" }, interestChip(note.interested)].filter(Boolean);
};

// The Students list's Contact cell (D13–D17), from the server's contactSummary on the student
// document: null when they were never contacted (count 0, or no field yet); otherwise the latest
// contact note's last chip (No reply, or the interest — "Replied" when it has none) and the line
// under it: the date, and "· N contacts" past the first.
export const contactCell = (summary, { timeZone } = {}) => {
  const count = Number(summary?.count || 0);
  if (count === 0) return null;
  const chip = contactChips({ type: "contact", replied: summary.replied, interested: summary.interested }).pop();
  const when = dateTimeLines(summary.lastAt, { timeZone });
  const sub = [when?.date, count > 1 && `${count} contacts`].filter(Boolean).join(" · ");
  return { chip, sub };
};

// The Contact report's filters (D11); `where` is the extra equality each adds to the query.
export const CONTACT_FILTERS = [
  { key: "all", label: "All", where: null },
  { key: "interested", label: "Interested", where: ["interested", "yes"] },
  { key: "notInterested", label: "Not interested", where: ["interested", "no"] },
  { key: "unsure", label: "Not sure", where: ["interested", "unsure"] },
  { key: "noReply", label: "No reply", where: ["replied", false] },
];

// The report's date range (D19). Today, 7 and 30 are calendar days in the viewer's own time
// zone, ending now; Custom is From 00:00 to To 23:59:59.999 local, either end optional.
export const RANGE_OPTIONS = [
  { key: "all", label: "All time" },
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "custom", label: "Custom" },
];
const RANGE_DAYS = { today: 1, "7d": 7, "30d": 30 };
export const RANGE_ERROR = "To must be on or after From.";

// "2026-10-01" (a date input's value) at local midnight, or null when empty or malformed.
const localDay = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

// { from, to } as Dates (null = open), plus `error` when a custom To falls before its From.
export const rangeFor = (key, { from = "", to = "" } = {}, now = new Date()) => {
  if (RANGE_DAYS[key]) {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (RANGE_DAYS[key] - 1));
    return { from: start, to: null }; // open end: a note written after the page opened still lists
  }
  if (key !== "custom") return { from: null, to: null };
  const start = localDay(from);
  const endDay = localDay(to);
  const end = endDay ? new Date(endDay.getFullYear(), endDay.getMonth(), endDay.getDate() + 1, 0, 0, 0, -1) : null;
  if (start && end && end < start) return { from: start, to: end, error: RANGE_ERROR };
  return { from: start, to: end };
};

// Search mode (D18) filters in memory: the same equality each CONTACT_FILTERS entry adds to the
// query, applied to the merged rows.
export const filterContactNotes = (rows, filterKey) => {
  const filter = CONTACT_FILTERS.find((f) => f.key === filterKey) || CONTACT_FILTERS[0];
  return filter.where ? rows.filter((r) => r[filter.where[0]] === filter.where[1]) : rows;
};

export const localContactCounts = (rows) => Object.fromEntries(CONTACT_FILTERS.map((f) => [f.key, filterContactNotes(rows, f.key).length]));

const noteTime = (n) => toDate(n.createdAt)?.getTime() || 0;

// Several students' note lists as one, newest first.
export const mergeContactNotes = (lists) => lists.flat().sort((a, b) => noteTime(b) - noteTime(a));

const RANGE_WORDS = { today: "today", "7d": "in the last 7 days", "30d": "in the last 30 days", custom: "in that date range" };

// The empty state's title: 'No contact notes for "Omar" in the last 7 days.'
export const contactEmptyText = ({ rangeKey = "all", term = "" } = {}) => {
  const parts = ["No contact notes", term && `for "${term}"`, RANGE_WORDS[rangeKey] || (!term && "yet")].filter(Boolean);
  return `${parts.join(" ")}.`;
};

// The fields a dialog writes for its form, in the rules' shape (D8): a general note is its text;
// a contact note carries `replied`, and `interested` only when they replied.
export const noteFields = ({ type, text, replied, interested }) =>
  type === "contact"
    ? { type: "contact", text, replied: Boolean(replied), ...(replied ? { interested } : {}) }
    : { type: "general", text };

// Whether the form may be saved: a general note needs text; a contact note needs the reply
// answered, the interest when they replied, and text within the limit (it may be empty).
export const noteFormValid = ({ type, text, replied, interested }, max) => {
  if (text.length > max) return false;
  if (type === "contact") return replied === false || (replied === true && Boolean(interestChip(interested)));
  return text.length > 0;
};
